import Checkbox from "@/common/components/Checkbox"
import MaskedTextField from "@/common/components/MaskedTextField"
import TextField from "@/common/components/TextField"
import { cpfMask, nameMask, phoneMask } from "@/common/constants/masks.constant"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { useReviewContact } from "@/features/establishmentLeadsFunnel/hooks/useReviewContact"
import { EstablishmentLeadsFunnelUpdateContactValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useIsMutating, useQueryClient } from "@tanstack/react-query"
import { FieldErrors, UseFormRegister } from "react-hook-form"
import ResendLeadContactEmailModal from "../../../common/components/ResendLeadContactEmailModal"
import { AxiosResponse } from "axios";

interface ContactPersonalInformationSectionProps {
  establishmentLeadId?: string
  contact?: ContactData
  register: UseFormRegister<EstablishmentLeadsFunnelUpdateContactValidationSchema>
  errors: FieldErrors<EstablishmentLeadsFunnelUpdateContactValidationSchema>
  showReviewButtons?: boolean
  updateLeadOnSuccess?: (reponse: AxiosResponse) => void
  disableFields?: boolean
}

export const ContactPersonalInformationSection = ({
  establishmentLeadId,
  contact,
  register,
  errors,
  showReviewButtons,
  updateLeadOnSuccess,
  disableFields,
}: ContactPersonalInformationSectionProps) => {
  const isMutating = !!useIsMutating();
  const queryClient = useQueryClient();

  const {
    mutate: approveContact,
    isError: approveContactIsError,
  } = useReviewContact({
    contactId: contact?.id,
    establishmentLeadId,
    state: 'approved',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineContact,
    isError: declineContactIsError,
  } = useReviewContact({
    contactId: contact?.id,
    establishmentLeadId,
    state: 'disapproved',
    onSuccess: updateLeadOnSuccess,
  })

  const isServerError = (approveContactIsError || declineContactIsError)

  return (
    <div className="flex w-full flex-col gap-4">
      {(isServerError) && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <div className="flex justify-between">
        <p className="text-lg font-bold">Dados pessoais</p>

        {showReviewButtons && (
          <ReviewButtons
            reviewState={contact?.last_review?.state}
            onApprove={(data?: { description?: string }) => { approveContact(data) }}
            onDecline={(data?: { description?: string }) => { declineContact(data) }}
            isPending={isMutating}
          />
        )}
      </div>
      {contact?.last_review?.state === 'disapproved' && (
        <RequestedReview description={contact?.last_review?.description} />
      )}
      <div className="w-full flex gap-2">
        <MaskedTextField
          label="Nome"
          markAsRequired
          mask={nameMask}
          className="w-2/3"
          defaultValue={contact?.full_name}
          errorMessage={errors?.contacts?.full_name?.message || undefined}
          {...register('contacts.full_name')}
          disabled={disableFields}
        />
        <TextField
          label="Cargo"
          markAsRequired
          maxLength={32}
          className="w-1/3"
          defaultValue={contact?.role_in_company}
          errorMessage={errors?.contacts?.role_in_company?.message || undefined}
          {...register('contacts.role_in_company')}
          disabled={disableFields}
        />
      </div>
      <div className="w-full flex gap-2">
        <MaskedTextField
          label="CPF"
          mask={cpfMask}
          className="w-2/3"
          defaultValue={contact?.cpf}
          errorMessage={errors?.contacts?.cpf?.message || undefined}
          {...register("contacts.cpf")}
          disabled={disableFields}
          markAsRequired
        />
        <MaskedTextField
          label="Telefone"
          markAsRequired
          mask={phoneMask}
          className="w-1/3"
          defaultValue={contact?.phone?.number}
          errorMessage={
            errors?.contacts?.phone_attributes?.number?.message || undefined
          }
          {...register("contacts.phone_attributes.number")}
          disabled={disableFields}
        />
      </div>
      <div className="flex gap-4 items-center">
        <TextField
          label="Email"
          className="w-full"
          markAsRequired
          value={contact?.email}
          errorMessage={errors?.contacts?.email?.message || undefined}
          {...register('contacts.email')}
          disabled={disableFields}
        />

        {contact?.state === ContactState.awaiting_client_documents && (
          <ResendLeadContactEmailModal contact={contact} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })} />
        )}
      </div>

      <Checkbox
        label="Preenche Dados Empresariais"
        errorMessage={errors?.contacts?.responsible_for_company_information?.message || undefined}
        {...register('contacts.responsible_for_company_information')}
        disabled={disableFields}
      />

    </div>
  )
}
